import { useEffect, useState } from 'react';
import * as React from 'react';
import {
  Button,
  ConfirmationModal,
  Notification,
  OnlyInDesktopTablet,
  OnlyInMobile,
  List,
} from '@components/shared';
import useStickyState from '@shared/useStickyState';
import { TourbookSummary, StoreState } from '@root/types';
import useToggle from '@root/shared/useToggle';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import tourbooksActions from '@store/actions/tourbooks';
import { RawSelect } from '@components/shared/forms';
import actions from '@store/actions';
import TourbookCard from '@components/shared/TourbookCard';
import EditTourbookModal from '@components/shared/EditTourbookModal';
import { tourbooksLoaded, getVisibleTourbookSummaries } from '@root/store/selectors';

import routes from '@root/routes';
import EmptyState from './EmptyState';
import styles from './Tourbooks.module.less';
import Layout from '../Layout';

type deleteCallbacks = {
  onSuccess: () => void;
  onError: () => void;
};

const mapState = (state: StoreState) => ({
  tourbooksLoading: !tourbooksLoaded(state),
  tourbooks: getVisibleTourbookSummaries(state),
});

const mapDispatch = {
  loadPage: () => tourbooksActions.manageTourbookPageLoad(),
  fetchTourbooks: (sort: string) => tourbooksActions.fetchTourbookSummaries(sort),
  deleteTourbook: (tourbook: any, callbacks: deleteCallbacks) =>
    tourbooksActions.deleteTourbook(tourbook, callbacks),
  clickedToTourbook: () => actions.clickedToTourbook(),
};

const emptyTourbook = {
  id: '',
  name: '',
  clientLogoCloudinaryId: '',
  headerCloudinaryId: '',
  clientName: '',
};

const connector = connect(mapState, mapDispatch);
export type ReduxProps = ConnectedProps<typeof connector>;

export const Tourbooks = ({
  loadPage,
  tourbooksLoading,
  tourbooks,
  deleteTourbook,
  clickedToTourbook,
  fetchTourbooks,
}: ReduxProps) => {
  const history = useHistory();

  const {
    value: isCreateModalOpen,
    setTrue: openCreateModal,
    setFalse: closeCreateModal,
  } = useToggle();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tourbookToDelete, setTourbookToDelete] = useState({});
  const [sortValue, setSortValue] = useStickyState('newest', 'tourbooks_sort');
  const { t } = useTranslation('tourbook');

  useEffect(() => {
    loadPage();
  }, []);

  useEffect(() => {
    fetchTourbooks(sortValue);
  }, [sortValue]);

  const onConfirmDeleteTourbook = () => {
    deleteTourbook(tourbookToDelete, {
      onSuccess: () => {
        Notification.info({
          title: t('notification.deleteSuccess'),
          placement: 'bottomLeft',
          duration: 3.5,
        });
        setIsModalOpen(false);
      },
      onError: () => {
        Notification.info({
          title: t('notification.deleteError'),
          placement: 'bottomLeft',
          duration: 3.5,
        });
      },
    });
  };

  const openModal = (tourbook: TourbookSummary, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setTourbookToDelete(tourbook);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const createTourbook = () => {
    openCreateModal();
  };

  const onCreateSuccess = (tourbook: TourbookSummary) => {
    history.push(routes.tourbook(tourbook.id));
  };

  const createTourbookModal = (
    <EditTourbookModal
      tourbook={emptyTourbook}
      isModalOpen={isCreateModalOpen}
      onCloseModal={closeCreateModal}
      onCreateSuccess={onCreateSuccess}
      actionType="create"
    />
  );

  if (tourbooksLoading) {
    return <div>{t('common:loading')}</div>;
  }

  if (!tourbooks.length) {
    return (
      <div>
        <EmptyState openCreateModal={openCreateModal} />
        {createTourbookModal}
      </div>
    );
  }

  const handleSortChange = (newSortValue: string) => {
    setSortValue(newSortValue);
  };

  const sortDropdown = () => {
    return (
      <RawSelect
        onChange={val => handleSortChange(val as string)}
        value={sortValue}
        name="tourbook-sort-select"
        className={styles.sortDropdownContainer}
        data-testid="tourbooks-sort-select"
        options={[
          { value: 'newest', label: t('newest') },
          { value: 'oldest', label: t('oldest') },
          { value: 'nameaz', label: t('nameAZ') },
          { value: 'nameza', label: t('nameZA') },
        ]}
      />
    );
  };
  return (
    <div>
      <ConfirmationModal
        isOpen={isModalOpen}
        title={t('deleteModal.title')}
        confirmationText={t('deleteModal.confirmationText')}
        cancelButtonText={t('common:cancel')}
        confirmButtonText={t('common:delete')}
        onClose={closeModal}
        onConfirm={onConfirmDeleteTourbook}
      />
      <div className={styles.listContainer}>
        <Layout activePage="myTourbooks" mobileFullWidth>
          <div className={styles.header}>
            <div className={styles.titleSortContainer}>
              <h1 className={styles.title}>{t('myTourbooks')}</h1>
              <OnlyInDesktopTablet>{sortDropdown()}</OnlyInDesktopTablet>
            </div>
            <OnlyInMobile>
              <Button
                className={styles.tourbookButton}
                data-testid="addTourbookButton"
                type="primary"
                size="small"
                onClick={() => {
                  createTourbook();
                }}
              >
                <span className={styles.buttonText}>{t('createTourbook')}</span>
              </Button>
            </OnlyInMobile>
            <OnlyInDesktopTablet>
              <Button
                className={styles.tourbookButton}
                data-testid="addTourbookButton"
                type="primary"
                size="large"
                onClick={() => {
                  createTourbook();
                }}
              >
                <span className={styles.buttonText}>{t('createTourbook')}</span>
              </Button>
            </OnlyInDesktopTablet>
          </div>
          <OnlyInMobile className={styles.sortDropdownMobileContainer}>
            {sortDropdown()}
          </OnlyInMobile>
          <List<TourbookSummary>
            className={styles.cardList}
            itemClassName={styles.card}
            items={tourbooks}
            renderItem={tourbookSummary => (
              <Link
                to={routes.tourbook(tourbookSummary.id)}
                data-testid={`${tourbookSummary.name}`}
                onClick={clickedToTourbook}
              >
                <TourbookCard
                  tourbook={tourbookSummary}
                  onDelete={event => openModal(tourbookSummary, event)}
                />
              </Link>
            )}
          />
        </Layout>
      </div>
      {createTourbookModal}
    </div>
  );
};

export default connector(Tourbooks);
